"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.webflowActions = exports.WEBFLOW_BG = exports.WEBFLOW_COLOR = exports.WEBFLOW_CATEGORY = exports.webflowActionsArgsSchema = exports.WebflowAction = exports.webflowUpdateArticleResultSchema = exports.webflowUpdateArticleArgsSchema = exports.webflowCreateArticleResultSchema = exports.webflowCreateArticleArgsSchema = exports.webflowGetArticleResultSchema = exports.webflowGetArticleArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Get Article ----------
exports.webflowGetArticleArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Webflow CMS collection (e.g., "Blog Posts")'),
    slug: zod_1.z.string().describe('URL slug of the item to retrieve'),
});
exports.webflowGetArticleResultSchema = zod_1.z.object({
    id: zod_1.z.string(),
    slug: zod_1.z.string().nullish(),
    isDraft: zod_1.z.boolean(),
    isArchived: zod_1.z.boolean(),
    fieldData: zod_1.z.record(zod_1.z.any()),
});
// ---------- Create Article ----------
exports.webflowCreateArticleArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Webflow CMS collection (e.g., "Blog Posts")'),
    slug: zod_1.z.string().describe('URL-friendly slug for the item'),
    isDraft: zod_1.z.boolean().default(true).describe('Whether the item is a draft (default: true)'),
    isArchived: zod_1.z.boolean().default(false).describe('Whether the item is archived (default: false)'),
    fieldData: zod_1.z
        .record(zod_1.z.any())
        .describe('Field data for the item (additional fields beyond name and slug). Required fields: post-body, post-decription, hero-image, category, name, is-this-featured'),
});
exports.webflowCreateArticleResultSchema = zod_1.z.object({
    id: zod_1.z.string(),
    slug: zod_1.z.string().nullish(),
    isDraft: zod_1.z.boolean(),
    isArchived: zod_1.z.boolean(),
});
// ---------- Update Article ----------
// Discriminated union for content update types
var fullFieldUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('full'),
    fieldData: zod_1.z.record(zod_1.z.any()).describe('Complete field data (replaces all fields)'),
});
var changesFieldUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('changes'),
    changes: zod_1.z.array(common_1.contentChangeSchema).min(1).describe('Apply incremental text changes'),
});
var partialFieldUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('partial'),
    fieldData: zod_1.z.record(zod_1.z.any()).describe('Partial field data (updates only specified fields)'),
});
exports.webflowUpdateArticleArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Webflow CMS collection (e.g., "Blog Posts")'),
    slug: zod_1.z.string().describe('URL slug of the item to update'),
    newSlug: zod_1.z.string().nullish().describe('New slug for the item (optional)'),
    isDraft: zod_1.z.boolean().nullish().describe('Whether the item should be a draft'),
    isArchived: zod_1.z.boolean().nullish().describe('Whether the item should be archived'),
    fieldUpdate: zod_1.z
        .discriminatedUnion('updateType', [fullFieldUpdate, changesFieldUpdate, partialFieldUpdate])
        .nullish()
        .describe('How to update item fields (mutually exclusive options)'),
});
exports.webflowUpdateArticleResultSchema = zod_1.z.object({
    id: zod_1.z.string(),
    slug: zod_1.z.string().nullish(),
    isDraft: zod_1.z.boolean(),
    isArchived: zod_1.z.boolean(),
    updated: zod_1.z.boolean(),
});
// ==========================
// Compound Tool: webflowActions
// ==========================
exports.WebflowAction = zod_1.z.enum(['getArticle', 'createArticle', 'updateArticle']);
exports.webflowActionsArgsSchema = zod_1.z.object({
    siteUrl: zod_1.z
        .string()
        .describe('Webflow site URL (e.g., "my-site.webflow.io" or "www.example.com"). Used to identify which site to work with.'),
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('getArticle'), params: exports.webflowGetArticleArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('createArticle'), params: exports.webflowCreateArticleArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateArticle'), params: exports.webflowUpdateArticleArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
// Union of all possible result shapes
var WebflowActionsAllResultsSchema = zod_1.z.union([
    exports.webflowGetArticleResultSchema,
    exports.webflowCreateArticleResultSchema,
    exports.webflowUpdateArticleResultSchema,
]);
// Dynamic result schemas keyed by action
var webflowActionsResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'getArticle' }, schema: exports.webflowGetArticleResultSchema },
        { when: { 'action.name': 'createArticle' }, schema: exports.webflowCreateArticleResultSchema },
        { when: { 'action.name': 'updateArticle' }, schema: exports.webflowUpdateArticleResultSchema },
    ],
    defaultSchema: WebflowActionsAllResultsSchema,
});
exports.WEBFLOW_CATEGORY = common_1.ToolCategorySchema.enum.integration;
exports.WEBFLOW_COLOR = common_1.BRAND_COLORS.WEBFLOW;
exports.WEBFLOW_BG = (0, common_1.createBgColor)(exports.WEBFLOW_COLOR);
// Compound tool metadata
exports.webflowActions = (0, common_1.createToolMetadata)({
    id: 'webflowActions',
    displayName: 'Webflow CMS',
    displayDescription: 'Manage Webflow CMS collections and items',
    category: exports.WEBFLOW_CATEGORY,
    color: exports.WEBFLOW_COLOR,
    backgroundColor: exports.WEBFLOW_BG,
    icon: 'webflow',
    argsSchema: exports.webflowActionsArgsSchema,
}, webflowActionsResultSchemas);
